import { DateValue } from '@razorpay/blade/components';
import {
  UpdatePayRequest,
  UpdatePayWithSalaryStructureRequest,
  RearchComponentInput,
} from 'api/gql-utils/payDetails/types';
import {
  ArrearApplicabilityDetailsData,
  PayDetailsBulkPreviewUserResult,
  PayrollCycle,
} from 'api/gql-utils/user/types';
import { getCurrentPayrollMonth } from 'components/RunPayroll/utils/utils';
import { format, getDate, isSameMonth } from 'date-fns';
import { convertToOrdinal, dateFormats } from 'utils/Dates';
import { SALARY_PAY_TYPES } from './constants';
import { CtcBreakdownData } from 'components/PayDetails/types';
import {
  SalaryStructureComponent,
  SalaryStructureComponentValueType,
} from 'components/SalaryStructure/types';
import {
  SalaryStructureComponentValueTypeLabels,
  statutoryComponentNameMap,
} from 'components/SalaryStructures/creation';
import { paiseAmountToRupee } from 'utils/Numbers';
import { salaryStrcutureComponentValueType } from 'components/SalaryStructure/constants';

export const createUpdatePayPayload = ({
  userId,
  annualSalary,
  effectiveFrom,
}: {
  userId: string;
  annualSalary: number;
  effectiveFrom: string;
}): UpdatePayRequest => {
  return {
    userId,
    payload: {
      annualSalary,
      effectiveFrom,
      allowance: [],
      basicSalary: 0,
      da: 0,
      hra: 0,
      deductions: [],
      employerESIContribution: 0,
      employerPFContribution: 0,
      isEligibleForEmployerESIContribution: false,
      isEligibleForEmployerPFContribution: false,
      isCustomSalaryStructure: false,
      lta: 0,
      flexiPartnerAllowanceAmount: 0,
      npsEmployerContributionLimit: 0,
      specialAllowance: 0,
      variablePay: '0',
      vpf: 0,
    },
  };
};

export const createUpdatePayWithSalaryStructurePayload = ({
  userId,
  annualSalary,
  effectiveFrom,
  salaryStructureId,
  requestId,
  hasStructureChange,
  rearchComponents,
}: {
  userId: string;
  annualSalary: number;
  effectiveFrom: string;
  salaryStructureId?: string;
  requestId: string;
  hasStructureChange?: boolean;
  rearchComponents?: RearchComponentInput[];
}): UpdatePayWithSalaryStructureRequest => {
  return {
    userId,
    payload: {
      salaryStructureId,
      annualSalary,
      effectiveFrom,
      requestId,
      hasStructureChange,
      rearchComponents,
      allowance: [],
      basicSalary: 0,
      da: 0,
      hra: 0,
      deductions: [],
      employerESIContribution: 0,
      employerPFContribution: 0,
      isEligibleForEmployerESIContribution: false,
      isEligibleForEmployerPFContribution: false,
      isCustomSalaryStructure: false,
      lta: 0,
      flexiPartnerAllowanceAmount: 0,
      npsEmployerContributionLimit: 0,
      specialAllowance: 0,
      variablePay: '0',
      vpf: 0,
    },
  };
};

export const getRevisionAlertMessageText = (
  payrollCycle: PayrollCycle,
  selectedEffectiveDate: DateValue,
) => {
  const payrollCycleStartDay = payrollCycle.startDate === 0 ? 1 : payrollCycle.startDate;

  if (selectedEffectiveDate) {
    const effectiveDay = getDate(selectedEffectiveDate);
    return `Previous salary would be considered from ${convertToOrdinal(
      payrollCycleStartDay,
    )} to ${convertToOrdinal(
      effectiveDay,
    )}. Revised compensation will be considered from ${convertToOrdinal(effectiveDay)}.`;
  }

  return '';
};

export const getArrearMonthsAlertText = (arrearDetails: ArrearApplicabilityDetailsData) => {
  const formattedMonths = arrearDetails.arrearApplicableMonths.map((month) =>
    format(new Date(month), dateFormats.monthYear),
  );

  const sourceMonths =
    formattedMonths.length > 1
      ? `${formattedMonths.slice(0, -1).join(', ')} and ${
          formattedMonths[formattedMonths.length - 1]
        }`
      : formattedMonths[0];

  const targetMonth = format(new Date(arrearDetails.arrearPaymentMonth), dateFormats.monthYear);

  return `Arrears will be calculated for the month of ${sourceMonths} and will be added to ${targetMonth} Payroll`;
};

export const checkIsCurrentPayrollMonth = (selectedEffectiveDate: Date) => {
  const currentPayrollMonth = getCurrentPayrollMonth();
  return isSameMonth(currentPayrollMonth, selectedEffectiveDate);
};

export const isDateAfterPayrollCycleStartDate = (
  selectedEffectiveDate: Date,
  payrollCycle: PayrollCycle,
) => {
  return getDate(selectedEffectiveDate) > payrollCycle.startDate;
};

export const transformBulkPreviewData = ({
  bulkPreviewData,
  requestId,
  isSalaryStructureUpdated,
}: {
  bulkPreviewData: PayDetailsBulkPreviewUserResult;
  requestId: string;
  isSalaryStructureUpdated: boolean;
}) => {
  const { earnings, compliances, earningArrears, complianceArrears, arrearSources } =
    bulkPreviewData;

  const fixedEarnings = earnings.filter((e) => e.payType === SALARY_PAY_TYPES.FIXED);
  const variableEarnings = earnings.filter((e) => e.payType === SALARY_PAY_TYPES.VARIABLE);
  const npbEarnings = earnings.filter((e) => e.payType === SALARY_PAY_TYPES.NON_PAYABLE);

  const mapToComponent = (item: {
    displayName: string;
    annualAmount?: number;
    amount?: number;
  }) => ({
    id: item.displayName,
    componentName: item.displayName,
    perYearAmount: item.annualAmount ?? item.amount ?? 0,
  });

  const mapToComplianceComponent = (item: {
    displayName: string;
    annualAmount?: number;
    amount?: number;
  }) => ({
    id: item.displayName,
    componentName:
      statutoryComponentNameMap[item.displayName as keyof typeof statutoryComponentNameMap] ??
      item.displayName,
    perYearAmount: item.annualAmount ?? item.amount ?? 0,
  });

  const transformedArrears = [
    ...earningArrears
      .sort((component1, component2) => {
        // Basic comes first
        if (component1.displayName === 'Basic') return -1;
        if (component2.displayName === 'Basic') return 1;

        // Special Allowance comes just before compliance
        if (component1.displayName === 'Special Allowance') return 1;
        if (component2.displayName === 'Special Allowance') return -1;

        return 0;
      })
      .map(mapToComponent),
    ...complianceArrears.map(mapToComplianceComponent),
  ];

  const transformedData: CtcBreakdownData = {
    requestId,
    hasStructureChange: isSalaryStructureUpdated,
    fixed: {
      data: fixedEarnings.map((earning) => ({
        id: earning.componentName,
        componentName: earning.displayName,
        perMonthAmount: earning.monthlyAmount || 0,
        perYearAmount: earning.annualAmount,
      })),
      totalMonthlyAmount: fixedEarnings.reduce((sum, e) => sum + (e.monthlyAmount || 0), 0),
      totalYearlyAmount: fixedEarnings.reduce((sum, e) => sum + e.annualAmount, 0),
    },
    variable: {
      data: variableEarnings.map((earning) => ({
        id: earning.componentName,
        componentName: earning.displayName,
        perYearAmount: earning.annualAmount,
      })),
      totalYearlyAmount: variableEarnings.reduce((sum, e) => sum + e.annualAmount, 0),
    },
    otherCtc: {
      data: [...npbEarnings.map(mapToComponent), ...compliances.map(mapToComplianceComponent)],
      totalYearlyAmount:
        npbEarnings.reduce((sum, e) => sum + e.annualAmount, 0) +
        compliances.reduce((sum, c) => sum + c.annualAmount, 0),
    },
    arrears: {
      data: transformedArrears,
      arrearSources,
      totalYearlyAmount:
        earningArrears.reduce((sum, arrear) => sum + arrear.amount, 0) +
        complianceArrears.reduce((sum, arrear) => sum + arrear.amount, 0),
    },
  };

  return transformedData;
};

export const getComponentFormula = (salaryComponent: SalaryStructureComponent) => {
  const valueType = salaryComponent.valueType as SalaryStructureComponentValueType;
  if (valueType === salaryStrcutureComponentValueType.SALARY_STRUCTURE_COMPONENT_VALUE_TYPE_SA)
    return 'Residual Amount';

  let value: number | string = salaryComponent.formula || 0;
  const suffix = SalaryStructureComponentValueTypeLabels[valueType]?.['label'];

  if (valueType === salaryStrcutureComponentValueType.SALARY_STRUCTURE_COMPONENT_VALUE_TYPE_FIXED) {
    value = paiseAmountToRupee(Number(value));
  }

  return `${value} ${suffix}`;
};
